/**
 * MUI component overrides — Figma-aligned defaults.
 * Use radius('…') pixel strings so values are never multiplied by shape.borderRadius.
 */
import type { Components, Theme } from '@mui/material/styles';

import { radius } from './shape';

export const componentOverrides: Components<Omit<Theme, 'components'>> = {
  MuiCssBaseline: {
    styleOverrides: (theme) => ({
      body: {
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
        fontFamily: theme.typography.fontFamily,
      },
      // A visible keyboard-focus ring was previously only reliable on
      // Button (its own default focus styling) — every other interactive
      // element (links, selects, checkboxes, menu items, custom controls)
      // had no consistent equivalent. `:focus-visible` only engages for
      // keyboard/programmatic focus, not mouse clicks, so this doesn't
      // add a ring on every click.
      '*:focus-visible': {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: 2,
      },
    }),
  },

  // Every Button/IconButton/MenuItem/Tab/etc. extends ButtonBase, which sets
  // its own `outline: 0` at the same CSS specificity as the global rule
  // above — same-specificity rules resolve by source order, and
  // ButtonBase's styled-component styles are injected after CssBaseline's,
  // so without this override buttons alone would lose the ring the rest of
  // the app just gained. `.Mui-focusVisible` is the class MUI itself
  // toggles for this state, so targeting it here reliably wins regardless
  // of insertion order (two classes beats ButtonBase's one).
  MuiButtonBase: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&.Mui-focusVisible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
      }),
    },
  },

  MuiButton: {
    defaultProps: {
      disableElevation: true,
      variant: 'contained',
    },
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.button,
        borderRadius: radius('pill'),
        paddingInline: theme.spacing(3.5),
        paddingBlock: theme.spacing(1.5),
        minHeight: 44,
      }),
      sizeMedium: {
        minHeight: 44,
      },
      containedPrimary: ({ theme }) => ({
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        fontFamily: theme.typography.button.fontFamily,
        fontWeight: 500,
        '&:hover': {
          backgroundColor: theme.palette.primary.dark,
        },
      }),
      containedSecondary: ({ theme }) => ({
        backgroundColor: theme.palette.secondary.main,
        color: theme.palette.secondary.contrastText,
        fontFamily: theme.typography.button.fontFamily,
        fontWeight: 400,
        fontSize: '1.125rem',
        '&:hover': {
          backgroundColor: theme.palette.secondary.dark,
        },
      }),
      outlined: ({ theme }) => ({
        borderColor: theme.palette.primary.main,
        color: theme.palette.primary.main,
        borderWidth: 1,
        borderRadius: radius('pill'),
        fontFamily: theme.typography.button.fontFamily,
        '&:hover': {
          borderWidth: 1,
          backgroundColor: theme.palette.action.hover,
        },
      }),
    },
  },

  MuiPaper: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: radius('md'),
        backgroundColor: theme.palette.surface.card,
      }),
    },
  },

  MuiTextField: {
    defaultProps: {
      variant: 'standard',
      fullWidth: true,
    },
  },

  MuiInputLabel: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.fieldLabel,
        color: theme.palette.text.secondary,
        position: 'static',
        transform: 'none',
        marginBottom: theme.spacing(0.5),
      }),
    },
  },

  MuiSelect: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.body1,
        fontSize: '1rem',
        lineHeight: '1.5rem',
        color: theme.palette.surface.placeholder,
        fontWeight: theme.typography.fontWeightRegular,
      }),
      select: ({ theme }) => ({
        paddingBlock: 0,
        paddingLeft: 0,
        '&:focus': { backgroundColor: 'transparent' },
        // The standard-variant underline that would normally show focus is
        // suppressed (see MuiInput below), and MUI's own NativeSelect base
        // style sets a plain `:focus { outline: 0 }` here at the same
        // specificity as the global focus-visible rule — so without this,
        // our Select fields (Age/Amount/Location) were the one place
        // tabbing gave no visible indication at all.
        '&:focus-visible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
      }),
      icon: ({ theme }) => ({
        color: theme.palette.text.primary,
      }),
    },
  },

  MuiInput: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.body1,
        fontSize: '1rem',
        lineHeight: '1.5rem',
        color: theme.palette.text.primary,
        '&::before, &::after': { display: 'none' },
      }),
      input: ({ theme }) => ({
        paddingBlock: 0,
        '&::placeholder': {
          color: theme.palette.surface.placeholder,
          opacity: 1,
        },
      }),
    },
  },

  MuiMenu: {
    styleOverrides: {
      paper: {
        borderRadius: radius('md'),
      },
    },
  },

  MuiMenuItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: radius('sm'),
        fontFamily: theme.typography.fontFamily,
        fontSize: '1rem',
        lineHeight: '1.5rem',
      }),
    },
  },

  MuiChip: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: radius('pill'),
        backgroundColor: theme.palette.surface.card,
        color: theme.palette.primary.main,
        paddingInline: theme.spacing(2),
        height: 48,
        fontFamily: theme.typography.fontFamily,
        fontWeight: theme.typography.fontWeightMedium,
        fontSize: '1.125rem',
      }),
    },
  },

  MuiLink: {
    defaultProps: { underline: 'hover' },
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.palette.primary.main,
        fontFamily: theme.typography.linkStrong.fontFamily,
        fontWeight: theme.typography.fontWeightBold,
      }),
    },
  },

  MuiDivider: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderColor: theme.palette.divider,
      }),
    },
  },
};
